<template>
  <div>
    <b-row class="align-items-center justify-content-between g-0">
      <b-container class="user-about mt-4">
        <b-form @submit.prevent="handleSubmit" @reset.prevent="handleReset" id="profileForm" class="bg-white">
          <h4 class="text-info text-center font-weight-bolder mb-4">Edit Contact Details</h4>

          <!-- Sticky Save & Cancel Buttons -->
          <b-form-group class="text-center sticky-top bg-white py-2">
            <b-button type="submit" variant="success" size="sm" :disabled="isSubmitting">
              {{ isSubmitting ? 'Please Wait...' : 'Save' }}
            </b-button>
            <b-button type="reset" variant="danger" size="sm" class="ml-2" :disabled="isSubmitting">
              Cancel
            </b-button>
          </b-form-group>

          <b-row>
            <!-- Email -->
            <b-col md="6">
              <b-form-group label="Email" label-for="email-input" class="mb-2">
                <b-form-input id="email-input" v-model="userProfile.email" size="sm"
                  :class="{ 'is-invalid': responseErrors.email || emailError }" @blur="validateEmail"></b-form-input>
                <b-form-invalid-feedback v-if="responseErrors.email">{{ responseErrors.email[0] }}</b-form-invalid-feedback>
                <b-form-invalid-feedback v-if="emailError">{{ emailError }}</b-form-invalid-feedback>
              </b-form-group>
            </b-col>

            <!-- Mobile Number -->
            <b-col md="6">
              <b-form-group label="Mobile" label-for="mobile-input" class="mb-2">
                <b-form-input id="mobile-input" v-model="userProfile.mobile" size="sm"
                  :class="{ 'is-invalid': responseErrors.mobile || mobileError }" @blur="validateMobile"></b-form-input>
                <b-form-invalid-feedback v-if="responseErrors.mobile">{{ responseErrors.mobile[0] }}</b-form-invalid-feedback>
                <b-form-invalid-feedback v-if="mobileError">{{ mobileError }}</b-form-invalid-feedback>
              </b-form-group>
            </b-col>
          </b-row>

          <!-- Present Address -->
          <h5 class="text-primary mt-3">Present Address</h5>
          <b-row>
            <!-- House Name -->
            <b-col md="6">
              <b-form-group label="House Name" class="mb-2">
                <b-form-input v-model="userProfile.present_house_name" size="sm"
                  :class="{ 'is-invalid': responseErrors.present_house_name }"></b-form-input>
                <b-form-invalid-feedback v-if="responseErrors.present_house_name">
                  {{ responseErrors.present_house_name[0] }}
                </b-form-invalid-feedback>
              </b-form-group>
            </b-col>
            <!-- Town -->
            <b-col md="6">
              <b-form-group label="Town" class="mb-2">
                <b-form-input v-model="userProfile.present_town" size="sm"
                  :class="{ 'is-invalid': responseErrors.present_town }"></b-form-input>
                <b-form-invalid-feedback v-if="responseErrors.present_town">
                  {{ responseErrors.present_town[0] }}
                </b-form-invalid-feedback>
              </b-form-group>
            </b-col>
          </b-row>

          <b-row>
            <!-- Pincode -->
            <b-col md="6">
              <b-form-group label="Pincode" class="mb-2">
                <b-form-input v-model="userProfile.present_pincode" size="sm"
                  :class="{ 'is-invalid': responseErrors.present_pincode }"></b-form-input>
                <b-form-invalid-feedback v-if="responseErrors.present_pincode">
                  {{ responseErrors.present_pincode[0] }}
                </b-form-invalid-feedback>
              </b-form-group>
            </b-col>
            <!-- Country -->
            <b-col md="6">
              <b-form-group label="Country" class="mb-2">
                <b-form-select v-model="userProfile.present_country" @change="updateStates('present')">
                  <option value="">Select Country</option>
                  <option v-for="country in regions" :key="country.slug" :value="country.name">
                    {{ country.name }}
                  </option>
                </b-form-select>
              </b-form-group>
            </b-col>
          </b-row>

          <b-row>
            <!-- State -->
            <b-col md="6">
              <b-form-group label="State" class="mb-2">
                <b-form-select v-model="userProfile.present_state" @change="updateDistricts('present')">
                  <option value="">Select State</option>
                  <option v-for="state in getStates('present')" :key="state.slug" :value="state.name">
                    {{ state.name }}
                  </option>
                </b-form-select>
              </b-form-group>
            </b-col>
            <!-- District -->
            <b-col md="6">
              <b-form-group label="District" class="mb-2">
                <b-form-select v-model="userProfile.present_district">
                  <option value="">Select District</option>
                  <option v-for="district in getDistricts('present')" :key="district.slug" :value="district.name">
                    {{ district.name }}
                  </option>
                </b-form-select>
              </b-form-group>
            </b-col>
          </b-row>

          <!-- Permanent Address -->
          <h5 class="text-primary mt-3">Permanent Address</h5>
          <b-row>
            <!-- House Name -->
            <b-col md="6">
              <b-form-group label="House Name" class="mb-2">
                <b-form-input v-model="userProfile.permanent_house_name" size="sm"
                  :class="{ 'is-invalid': responseErrors.permanent_house_name }"></b-form-input>
                <b-form-invalid-feedback v-if="responseErrors.permanent_house_name">
                  {{ responseErrors.permanent_house_name[0] }}
                </b-form-invalid-feedback>
              </b-form-group>
            </b-col>
            <!-- Town -->
            <b-col md="6">
              <b-form-group label="Town" class="mb-2">
                <b-form-input v-model="userProfile.permanent_town" size="sm"
                  :class="{ 'is-invalid': responseErrors.permanent_town }"></b-form-input>
                <b-form-invalid-feedback v-if="responseErrors.permanent_town">
                  {{ responseErrors.permanent_town[0] }}
                </b-form-invalid-feedback>
              </b-form-group>
            </b-col>
          </b-row>

          <b-row>
            <!-- Pincode -->
            <b-col md="6">
              <b-form-group label="Pincode" class="mb-2">
                <b-form-input v-model="userProfile.permanent_pincode" size="sm"
                  :class="{ 'is-invalid': responseErrors.permanent_pincode }"></b-form-input>
                <b-form-invalid-feedback v-if="responseErrors.permanent_pincode">
                  {{ responseErrors.permanent_pincode[0] }}
                </b-form-invalid-feedback>
              </b-form-group>
            </b-col>
            <!-- Country -->
            <b-col md="6">
              <b-form-group label="Country" class="mb-2">
                <b-form-select v-model="userProfile.permanent_country" @change="updateStates('permanent')">
                  <option value="">Select Country</option>
                  <option v-for="country in regions" :key="country.slug" :value="country.name">
                    {{ country.name }}
                  </option>
                </b-form-select>
              </b-form-group>
            </b-col>
          </b-row>

          <b-row>
            <!-- State -->
            <b-col md="6">
              <b-form-group label="State" class="mb-2">
                <b-form-select v-model="userProfile.permanent_state" @change="updateDistricts('permanent')">
                  <option value="">Select State</option>
                  <option v-for="state in getStates('permanent')" :key="state.slug" :value="state.name">
                    {{ state.name }}
                  </option>
                </b-form-select>
              </b-form-group>
            </b-col>
            <!-- District -->
            <b-col md="6">
              <b-form-group label="District" class="mb-2">
                <b-form-select v-model="userProfile.permanent_district">
                  <option value="">Select District</option>
                  <option v-for="district in getDistricts('permanent')" :key="district.slug" :value="district.name">
                    {{ district.name }}
                  </option>
                </b-form-select>
              </b-form-group>
            </b-col>
          </b-row>

          <!-- Office Address -->
          <h5 class="text-primary mt-3">Office Address</h5>
          <b-row>
            <!-- House Name -->
            <b-col md="6">
              <b-form-group label="House Name" class="mb-2">
                <b-form-input v-model="userProfile.office_house_name" size="sm"
                  :class="{ 'is-invalid': responseErrors.office_house_name }"></b-form-input>
                <b-form-invalid-feedback v-if="responseErrors.office_house_name">
                  {{ responseErrors.office_house_name[0] }}
                </b-form-invalid-feedback>
              </b-form-group>
            </b-col>
            <!-- Town -->
            <b-col md="6">
              <b-form-group label="Town" class="mb-2">
                <b-form-input v-model="userProfile.office_town" size="sm"
                  :class="{ 'is-invalid': responseErrors.office_town }"></b-form-input>
                <b-form-invalid-feedback v-if="responseErrors.office_town">
                  {{ responseErrors.office_town[0] }}
                </b-form-invalid-feedback>
              </b-form-group>
            </b-col>
          </b-row>

          <b-row>
            <!-- Pincode -->
            <b-col md="6">
              <b-form-group label="Pincode" class="mb-2">
                <b-form-input v-model="userProfile.office_pincode" size="sm"
                  :class="{ 'is-invalid': responseErrors.office_pincode }"></b-form-input>
                <b-form-invalid-feedback v-if="responseErrors.office_pincode">
                  {{ responseErrors.office_pincode[0] }}
                </b-form-invalid-feedback>
              </b-form-group>
            </b-col>
            <!-- Country -->
            <b-col md="6">
              <b-form-group label="Country" class="mb-2">
                <b-form-select v-model="userProfile.office_country" @change="updateStates('office')">
                  <option value="">Select Country</option>
                  <option v-for="country in regions" :key="country.slug" :value="country.name">
                    {{ country.name }}
                  </option>
                </b-form-select>
              </b-form-group>
            </b-col>
          </b-row>

          <b-row>
            <!-- State -->
            <b-col md="6">
              <b-form-group label="State" class="mb-2">
                <b-form-select v-model="userProfile.office_state" @change="updateDistricts('office')">
                  <option value="">Select State</option>
                  <option v-for="state in getStates('office')" :key="state.slug" :value="state.name">
                    {{ state.name }}
                  </option>
                </b-form-select>
              </b-form-group>
            </b-col>
            <!-- District -->
            <b-col md="6">
              <b-form-group label="District" class="mb-2">
                <b-form-select v-model="userProfile.office_district">
                  <option value="">Select District</option>
                  <option v-for="district in getDistricts('office')" :key="district.slug" :value="district.name">
                    {{ district.name }}
                  </option>
                </b-form-select>
              </b-form-group>
            </b-col>
          </b-row>
        </b-form>
      </b-container>
    </b-row>
  </div>
</template>

<script>
import { regions } from './constants/regions';

export default {
  props: {
    profile: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      regions,
      userProfile: { ...this.profile },
      responseErrors: {},
      isSubmitting: false,
      emailError: "",
      mobileError: "",
      textFields: [
        { key: 'house_name', label: 'House Name' },
        { key: 'town', label: 'Town' },
        { key: 'pincode', label: 'Pincode' }
      ]
    };
  },
  methods: {
    validateEmail() {
      const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
      this.emailError = emailPattern.test(this.userProfile.email) ? "" : "Invalid email format";
    },
    validateMobile() {
      const mobilePattern = /^[0-9]{10,}$/;
      this.mobileError = mobilePattern.test(this.userProfile.mobile) ? "" : "Mobile number must be at least 10 digits";
    },
    async handleSubmit() {
      this.validateEmail();
      this.validateMobile();
      if (this.emailError || this.mobileError) return;

      this.isSubmitting = true;
      try {
        await this.$store.dispatch('storeUserProfile', this.userProfile);
        this.$toast.success('Profile Updated', { position: 'top', duration: 3000 });
        this.$emit('profileUpdated');
      } catch (error) {
        this.responseErrors = error.response.data.errors || {};
      } finally {
        this.isSubmitting = false;
      }
    },
    handleReset() {
      this.userProfile = { ...this.profile };
      this.responseErrors = {};
      this.$emit('profileCancelled');
    },
    updateStates(type) {
      this.userProfile[`${type}_state`] = "";
      this.userProfile[`${type}_district`] = "";
    },
    updateDistricts(type) {
      this.userProfile[`${type}_district`] = "";
    },
    getStates(type) {
      const country = this.userProfile[`${type}_country`];
      if (!Array.isArray(regions)) {
        return [];
      }
      return regions.find((c) => c.name === country)?.states || [];
    },
    getDistricts(type) {
      if (!Array.isArray(regions)) {
        return [];
      }

      const country = this.userProfile[`${type}_country`];
      const state = this.userProfile[`${type}_state`];

      const foundCountry = regions.find((c) => c.name === country);
      if (!foundCountry) {
        return [];
      }

      if (!Array.isArray(foundCountry.states)) {
        return [];
      }

      const foundState = foundCountry.states.find((s) => s.name === state);
      if (!foundState) {
        return [];
      }

      return foundState.districts || [];
    }

  },
};
</script>
<style scoped>
.sticky-top {
  position: sticky;
  top: 0;
  z-index: 1000;
  border-bottom: 1px solid #ddd;
}
</style>
